import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";

/**
 * RulesEngineApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/rulesEngineApi/rulesEngineApi.service.js.
 *
 * Two sub-namespaces:
 *   apiService.rulesEngine.lifecycle.{init,list}
 *   apiService.rulesEngine.rule.{detail,create,update,updateTrigger,remove}
 *
 * Endpoint quirks preserved 1:1 with the legacy:
 *   - `lifecycle.init()` POSTs to `/lifecyclerules/init/true`
 *   - `rule.detail({id})` GETs `/rule s/<id>` (note the trailing `s` on the
 *     base — `service + 's/'` in the legacy)
 *   - `rule.create({templateId})` is a PUT (not POST) to `/rule/<templateId>`
 *   - `rule.update({...})` sends `Content-Type: text/plain` with the
 *     selected id as a raw string body
 *   - `rule.remove({id})` DELETEs `/ruleengine/rules/<id>` (full path,
 *     bypassing the inner `service`)
 */
const RULEENGINE_BASE_V2 = "/v2/api/ruleengine";

interface RuleUpdateProps {
  category: { id: number | string };
  rule: { section: string; selectedId: string };
}

interface RuleTriggerProps {
  id: number | string;
  startMonth: unknown;
  endMonth: unknown;
  monthsBeforeEnd: unknown;
  expirationBased: unknown;
}

@Injectable({ providedIn: "root" })
export class RulesEngineApiService {
  private readonly http = inject(HttpClient);

  readonly lifecycle = {
    init: (): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(
          `${RULEENGINE_BASE_V2}/lifecyclerules/init/true`,
          null,
        ),
      ),

    list: (): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${RULEENGINE_BASE_V2}/lifecyclerules`)),
  };

  readonly rule = {
    detail: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${RULEENGINE_BASE_V2}/rules/${props.id}`),
      ),

    create: (props: {
      templateId: number | string;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(
          `${RULEENGINE_BASE_V2}/rule/${props.templateId}`,
          null,
        ),
      ),

    update: (props: RuleUpdateProps): Promise<AjsResponse<unknown>> => {
      const headers = new HttpHeaders({ "Content-Type": "text/plain" });
      return wrapAjs(
        this.http.put<unknown>(
          `${RULEENGINE_BASE_V2}/template/${props.category.id}/${props.rule.section}`,
          props.rule.selectedId,
          { headers },
        ),
      );
    },

    updateTrigger: (props: RuleTriggerProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(
          `${RULEENGINE_BASE_V2}/rule/${props.id}/trigger/params`,
          {
            startMonth: props.startMonth,
            endMonth: props.endMonth,
            monthsBeforeEnd: props.monthsBeforeEnd,
            expirationBased: props.expirationBased,
          },
        ),
      ),

    remove: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.delete<unknown>(`${RULEENGINE_BASE_V2}/rules/${props.id}`),
      ),
  };
}
